import * as React from 'react';

type RowKind = 'eq' | 'del' | 'add' | 'blank';

interface IAlignedRow {
    left: string | null;
    leftKind: RowKind;
    right: string | null;
    rightKind: RowKind;
}

interface IOp {
    t: 'eq' | 'del' | 'add';
    l?: string;
    r?: string;
}

const diffLines = (oldLines: string[], newLines: string[]): IOp[] => {
    const n = oldLines.length;
    const m = newLines.length;

    // LCS length table.
    const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            dp[i][j] =
                oldLines[i] === newLines[j]
                    ? dp[i + 1][j + 1] + 1
                    : Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }

    const ops: IOp[] = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (oldLines[i] === newLines[j]) {
            ops.push({ t: 'eq', l: oldLines[i], r: newLines[j] });
            i++;
            j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            ops.push({ t: 'del', l: oldLines[i] });
            i++;
        } else {
            ops.push({ t: 'add', r: newLines[j] });
            j++;
        }
    }
    while (i < n) {
        ops.push({ t: 'del', l: oldLines[i] });
        i++;
    }
    while (j < m) {
        ops.push({ t: 'add', r: newLines[j] });
        j++;
    }
    return ops;
};

// Converts the op stream into aligned rows, pairing runs of deletes/adds so a
// modified line shows old on the left and new on the right, padding the shorter
// side with blank rows.
const alignRows = (ops: IOp[]): IAlignedRow[] => {
    const rows: IAlignedRow[] = [];
    let k = 0;
    while (k < ops.length) {
        if (ops[k].t === 'eq') {
            rows.push({ left: ops[k].l!, leftKind: 'eq', right: ops[k].r!, rightKind: 'eq' });
            k++;
        } else {
            const dels: string[] = [];
            const adds: string[] = [];
            while (k < ops.length && ops[k].t !== 'eq') {
                if (ops[k].t === 'del') dels.push(ops[k].l!);
                else adds.push(ops[k].r!);
                k++;
            }
            const max = Math.max(dels.length, adds.length);
            for (let x = 0; x < max; x++) {
                rows.push({
                    left: x < dels.length ? dels[x] : null,
                    leftKind: x < dels.length ? 'del' : 'blank',
                    right: x < adds.length ? adds[x] : null,
                    rightKind: x < adds.length ? 'add' : 'blank'
                });
            }
        }
    }
    return rows;
};

const splitLines = (json: string): string[] => (json == null ? [] : json.replace(/\r\n/g, '\n').split('\n'));

interface IJsonDiffProps {
    oldJson: string;
    newJson: string;
    leftWidthPct: number;
    onSplitterMouseDown: (e: React.MouseEvent) => void;
    containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Renders a side-by-side, line-aligned diff of two JSON strings with +/- gutters
 * and blank-line padding (GitHub split-diff style). Deleted lines are red on the
 * left, added lines green on the right, unchanged lines are shown on both sides.
 */
export default class JsonDiff extends React.Component<IJsonDiffProps> {
    private leftColRef = React.createRef<HTMLDivElement>();
    private rightColRef = React.createRef<HTMLDivElement>();
    private syncingFrom: 'left' | 'right' | null = null;

    syncScroll = (source: 'left' | 'right') => {
        // Prevent the mirrored scroll from re-triggering a sync loop.
        if (this.syncingFrom && this.syncingFrom !== source) return;
        const from = source === 'left' ? this.leftColRef.current : this.rightColRef.current;
        const to = source === 'left' ? this.rightColRef.current : this.leftColRef.current;
        if (!from || !to) return;
        this.syncingFrom = source;
        to.scrollTop = from.scrollTop;
        to.scrollLeft = from.scrollLeft;
        window.requestAnimationFrame(() => {
            this.syncingFrom = null;
        });
    };

    cellStyle(kind: RowKind): React.CSSProperties {
        const base: React.CSSProperties = {
            whiteSpace: 'pre',
            fontFamily: 'monospace',
            fontSize: '0.85em',
            lineHeight: '1.4em',
            minHeight: '1.4em',
            padding: '0 6px'
        };
        switch (kind) {
            case 'del':
                return { ...base, background: '#fdd4d4' };
            case 'add':
                return { ...base, background: '#d4f7d4' };
            case 'blank':
                return { ...base, background: '#f0f0f0' };
            default:
                return base;
        }
    }

    gutter(kind: RowKind): string {
        if (kind === 'del') return '- ';
        if (kind === 'add') return '+ ';
        if (kind === 'blank') return '';
        return '\u00a0\u00a0';
    }

    renderColumn(rows: IAlignedRow[], side: 'left' | 'right') {
        return rows.map((row, idx) => {
            const kind = side === 'left' ? row.leftKind : row.rightKind;
            const text = side === 'left' ? row.left : row.right;
            return (
                <div key={idx} style={this.cellStyle(kind)}>
                    {kind === 'blank' ? '\u00a0' : this.gutter(kind) + (text ?? '')}
                </div>
            );
        });
    }

    render() {
        const oldLines = splitLines(this.props.oldJson);
        const newLines = splitLines(this.props.newJson);
        const rows = alignRows(diffLines(oldLines, newLines));

        const columnScroll: React.CSSProperties = {
            overflow: 'auto',
            height: '100%',
            background: '#fff',
            border: '1px solid #e0e0e0'
        };

        return (
            <div ref={this.props.containerRef} style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                <div
                    ref={this.leftColRef}
                    onScroll={() => this.syncScroll('left')}
                    style={{ flex: `0 0 ${this.props.leftWidthPct}%`, minWidth: 0, ...columnScroll }}
                >
                    {this.renderColumn(rows, 'left')}
                </div>
                <div
                    onMouseDown={this.props.onSplitterMouseDown}
                    title="Drag to resize"
                    style={{
                        flex: '0 0 8px',
                        cursor: 'col-resize',
                        background: '#d0d0d0',
                        borderRadius: '2px'
                    }}
                />
                <div
                    ref={this.rightColRef}
                    onScroll={() => this.syncScroll('right')}
                    style={{ flex: '1 1 0%', minWidth: 0, ...columnScroll }}
                >
                    {this.renderColumn(rows, 'right')}
                </div>
            </div>
        );
    }
}
